var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getLocationStateBySlug, getLocationStatePathBySlug } from './locationStates';
import { parsePricingPath } from '../utils/pricingPath';
import { DEFAULT_MONTHLY_DISPLAY_RATE } from '../utils/pricing';
import { TRUCK_PARKING_BASE_PATH, buildTruckParkingCityPath, buildTruckParkingIntentPath, buildTruckParkingStatePath, parseLegacyDiscoveryPath, parseTruckParkingPath, } from '../utils/truckParkingDiscovery';
export var CANONICAL_BASE_URL = 'https://www.trucksparkinghub.com';
var DEFAULT_OG_IMAGE = "".concat(CANONICAL_BASE_URL, "/tph-logo-desktop.png");
var SEO_MONTHLY_RATE_FLOOR = DEFAULT_MONTHLY_DISPLAY_RATE;
var CITY_ROUTE_META_OVERRIDES = {
    'north-carolina/charlotte': {
        description: "Secure truck parking in Charlotte, NC near I-77 and I-85. 5 lots with 24/7 access, perimeter fencing, and cameras. Daily from $27 | Weekly $180 | Monthly $".concat(SEO_MONTHLY_RATE_FLOOR, "."),
    },
};
var STATE_ROUTE_META_OVERRIDES = {
    georgia: {
        description: "Secure truck parking in Georgia. Lots in Ellenwood and Cartersville with 24/7 access, fencing, and cameras. Daily from $27 | Weekly $180 | Monthly $".concat(SEO_MONTHLY_RATE_FLOOR, "."),
    },
};
var decodeSlugToken = function (value) {
    if (value === void 0) { value = ''; }
    return (String(value || '')
        .split('-')
        .filter(Boolean)
        .map(function (segment) { return segment.charAt(0).toUpperCase() + segment.slice(1); })
        .join(' '));
};
var normalizePathname = function (pathname) {
    var _a;
    if (pathname === void 0) { pathname = ''; }
    if (!pathname)
        return '/';
    var withoutQuery = ((_a = pathname.split('?')[0]) === null || _a === void 0 ? void 0 : _a.split('#')[0]) || '/';
    if (withoutQuery === '/')
        return '/';
    return withoutQuery.replace(/\/+$/, '') || '/';
};
var PUBLIC_STATIC_ROUTE_META = {
    '/': {
        routeKey: 'home',
        title: 'List Your Truck Parking Lot | Truck Parking Hub',
        description: 'Turn unused commercial space into truck parking revenue. List for $0, control rates and availability, or tell Truck Parking Hub where drivers need parking next.',
        canonicalPath: '/',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'home',
    },
    '/truck-parking': {
        routeKey: 'truck_parking',
        title: 'Truck Parking by State and City | Truck Parking Hub',
        description: 'Browse truck parking locations by state and city. Pick your state, find a city on your route, and move into live pricing and booking.',
        canonicalPath: TRUCK_PARKING_BASE_PATH,
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/list-your-lot': {
        routeKey: 'list_your_lot',
        title: 'List Your Truck Parking Property | Truck Parking Hub',
        description: 'Submit your property for a truck-access, market-demand, and parking-network review.',
        canonicalPath: '/list-your-lot',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
    },
    '/request-parking': {
        routeKey: 'request_parking',
        title: 'Request Truck Parking | Truck Parking Hub',
        description: 'Tell Truck Parking Hub where drivers and fleets need verified parking next.',
        canonicalPath: '/request-parking',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/rates': {
        routeKey: 'rates',
        title: 'Truck Parking Rates | Daily, Weekly, Monthly',
        description: 'Review transparent truck parking rates and savings options for daily, weekly, and monthly plans.',
        canonicalPath: '/rates',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'rates',
    },
    '/about': {
        routeKey: 'about',
        title: 'About Truck Parking Hub',
        description: 'Truck Parking Hub: gated 24/7 secured lots across NC, SC, GA, OH & PA for drivers, owner-operators, and fleets.',
        canonicalPath: '/about',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'AboutPage',
        sectionKey: 'about',
    },
    '/terms-and-conditions': {
        routeKey: 'terms_and_conditions',
        title: 'Truck Parking Terms and Conditions | Truck Parking Hub',
        description: 'Review truck parking terms and conditions, payment terms, liability limits, and contact information before booking.',
        canonicalPath: '/terms-and-conditions',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
    },
    '/privacy-policy': {
        routeKey: 'privacy_policy',
        title: 'Truck Parking Privacy Policy | Truck Parking Hub',
        description: 'Review how Truck Parking Hub handles truck parking booking, support, and hiring information across the public site.',
        canonicalPath: '/privacy-policy',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
    },
    '/contact': {
        routeKey: 'contact',
        title: 'Contact Truck Parking Hub | Support and Booking Help',
        description: 'Contact Truck Parking Hub for booking support, gated lot access questions, fleet parking, and driver hiring inquiries.',
        canonicalPath: '/contact',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'ContactPage',
    },
    '/pm-partners': {
        routeKey: 'pm_partners',
        title: 'Truck Parking and Repair Partners | PM Services Network',
        description: 'Find preventive maintenance and truck repair partners, compare service fit, and connect directly with trusted PM providers.',
        canonicalPath: '/pm-partners',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'CollectionPage',
        sectionKey: 'pm_partners',
    },
    '/truck-repair-and-maintenance': {
        routeKey: 'repair_hub',
        title: 'Truck Repair and Preventive Maintenance | Truck Parking Hub',
        description: 'Connect breakdown support, preventive maintenance, and dispatch-to-garage coordination with trusted service partners.',
        canonicalPath: '/truck-repair-and-maintenance',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'CollectionPage',
        sectionKey: 'pm_partners',
    },
    '/truck-repair-and-maintenance/preventive-maintenance': {
        routeKey: 'preventive_maintenance',
        title: 'Preventive Maintenance for Semi Trucks | Truck Parking Hub',
        description: 'Reduce downtime with structured PM scheduling, service coordination, and trusted diesel maintenance partners.',
        canonicalPath: '/truck-repair-and-maintenance/preventive-maintenance',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'Service',
        sectionKey: 'pm_partners',
    },
    '/truck-repair-and-maintenance/truck-breakdown-support': {
        routeKey: 'breakdown_support',
        title: 'Truck Breakdown Support and Repair Routing | Truck Parking Hub',
        description: 'Coordinate truck breakdown intake, dispatch communication, and repair status visibility from request to closeout.',
        canonicalPath: '/truck-repair-and-maintenance/truck-breakdown-support',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'Service',
        sectionKey: 'pm_partners',
    },
    '/truck-repair-and-maintenance/garage-platform-management': {
        routeKey: 'garage_platform',
        title: 'Garage Platform Management for Diesel Repair Teams',
        description: 'Improve garage workflow control, technician assignment, and repair transparency with platform-based operations.',
        canonicalPath: '/truck-repair-and-maintenance/garage-platform-management',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'SoftwareApplication',
        sectionKey: 'pm_partners',
    },
    '/truck-repair-and-maintenance/dispatch-garage-coordination': {
        routeKey: 'dispatch_garage_coordination',
        title: 'Dispatch and Garage Coordination for Fleet Repairs',
        description: 'Align dispatch, drivers, and garages with structured repair workflows, status updates, and closeout visibility.',
        canonicalPath: '/truck-repair-and-maintenance/dispatch-garage-coordination',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'Service',
        sectionKey: 'pm_partners',
    },
    '/truck-parking/last-hour-parking': {
        routeKey: 'last_hour_parking',
        title: 'Last-Hour Truck Parking | Find Secure Parking Fast',
        description: 'Need truck parking fast? Use this last-hour parking guide to verify a stop quickly, avoid wasted calls, and move straight into booking.',
        canonicalPath: '/truck-parking/last-hour-parking',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'HowTo',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-77': {
        routeKey: 'corridor_near_i77',
        title: 'Truck Parking Near I-77 | Charlotte, NC | Truck Parking Hub',
        description: 'Secure truck parking near I-77 in Charlotte, NC. Gated lots within 5 miles of I-77 exits at Raleigh Street, Robinson Circle, and Rozzelles Ferry. Book daily, weekly, or monthly.',
        canonicalPath: '/truck-parking/near-i-77',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-85': {
        routeKey: 'corridor_near_i85',
        title: 'Truck Parking Near I-85 | Charlotte NC & Ellenwood GA | Truck Parking Hub',
        description: 'Secure truck parking near I-85 in Charlotte NC and Ellenwood GA. Gated lots near I-85 exits on the Atlanta-to-Charlotte corridor. Book daily, weekly, or monthly.',
        canonicalPath: '/truck-parking/near-i-85',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-485': {
        routeKey: 'corridor_near_i485',
        title: 'Truck Parking Near I-485 | Charlotte, Matthews, Mint Hill | Truck Parking Hub',
        description: 'Secure truck parking near I-485 in Charlotte, Matthews, and Mint Hill NC. Three gated lots on the Charlotte outer belt: daily from $27, weekly from $180. Book online.',
        canonicalPath: '/truck-parking/near-i-485',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-us-29': {
        routeKey: 'corridor_near_us29',
        title: 'Truck Parking Near US-29 | Charlotte, NC | Truck Parking Hub',
        description: 'Secure truck parking near US-29 in Charlotte, NC. Gated lots near East Sugar Creek Road and US-29 industrial delivery zones. Book daily, weekly, or monthly.',
        canonicalPath: '/truck-parking/near-us-29',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-71': {
        routeKey: 'corridor_near_i71',
        title: 'Truck Parking Near I-71 | Columbus, OH | Truck Parking Hub',
        description: 'Secure truck parking near I-71 in Columbus, OH at Watkins Road. Gated lot near Exit 101, daily, weekly, and monthly parking available. Book online 24/7.',
        canonicalPath: '/truck-parking/near-i-71',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-279': {
        routeKey: 'corridor_near_i279',
        title: 'Truck Parking Near I-279 | Carnegie, PA | Truck Parking Hub',
        description: 'Secure truck parking near I-279 in Carnegie, PA, the last stop before Pittsburgh. Gated lot near I-79 and US-19. Daily, weekly, and monthly rates. Book online.',
        canonicalPath: '/truck-parking/near-i-279',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-75-georgia': {
        routeKey: 'corridor_near_i75_ga',
        title: 'Truck Parking Near I-75 Georgia | Cartersville & Ellenwood | Truck Parking Hub',
        description: 'Secure truck parking near I-75 in Georgia: Cartersville (Exit 290) daily from $14, Ellenwood near I-285. Stage for Atlanta or park on the Chattanooga corridor. Book online.',
        canonicalPath: '/truck-parking/near-i-75-georgia',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/truck-parking/near-i-285': {
        routeKey: 'corridor_near_i285',
        title: 'Truck Parking Near I-285 | Ellenwood, GA | Truck Parking Hub',
        description: 'Secure truck parking near I-285 in Ellenwood, GA. Gated lot near Exit 52-55 on the Atlanta perimeter. Daily, weekly, and monthly rates. Book online.',
        canonicalPath: '/truck-parking/near-i-285',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'WebPage',
        sectionKey: 'locations',
    },
    '/hiring': {
        routeKey: 'hiring_hub',
        title: 'We Hire Truck Drivers | OTR, Oversize, Box Truck, Amazon Relay',
        description: 'Choose your hiring route and apply for OTR, oversize/heavy haul, box truck, or Amazon Relay driver roles with quick intake and recruiter follow-up.',
        canonicalPath: '/hiring',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'CollectionPage',
    },
    '/hiring/otr': {
        routeKey: 'hiring_otr_driver',
        title: 'We Hire OTR Truck Drivers | Apply Fast',
        description: 'Apply for over-the-road driver roles with long-haul lanes, recruiter screening, and full background/MVR/drug-test intake.',
        canonicalPath: '/hiring/otr',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'JobPosting',
    },
    '/hiring/box-truck': {
        routeKey: 'hiring_box_truck_driver',
        title: 'We Hire Box Truck Drivers | 26ft Straight Truck Jobs',
        description: 'Apply for box truck and 26ft straight truck driver positions with quick intake, recruiter call options, and complete compliance screening.',
        canonicalPath: '/hiring/box-truck',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'JobPosting',
    },
    '/hiring/oversize': {
        routeKey: 'hiring_oversize_driver',
        title: 'We Hire Oversize and Heavy Haul Drivers | Apply Online',
        description: 'Apply for oversize and heavy haul driver roles with permit-aware routing, recruiter review, and structured pre-hire compliance steps.',
        canonicalPath: '/hiring/oversize',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'JobPosting',
    },
    '/hiring/amazon-relay': {
        routeKey: 'hiring_amazon_relay_driver',
        title: 'Amazon Relay Careers | Driver Intake and Hiring',
        description: 'Apply for Amazon Relay-focused driver opportunities with recruiter route-fit review, safety screening, and compliance onboarding.',
        canonicalPath: '/hiring/amazon-relay',
        ogImage: DEFAULT_OG_IMAGE,
        robots: 'index,follow',
        schemaType: 'JobPosting',
    },
};
var PRIVATE_ROUTE_PATTERNS = [
    /^\/manage-booking(?:\/|$)/i,
    /^\/checkout(?:\/|$)/i,
    /^\/login(?:\/|$)/i,
    /^\/signin(?:\/|$)/i,
    /^\/admin(?:\/|$)/i,
    /^\/callback(?:\/|$)/i,
    /^\/auth(?:\/|$)/i,
    /^\/m(?:\/|$)/i,
    /^\/extend(?:\/|$)/i,
    /^\/my-bookings(?:\/|$)/i,
    /^\/dashboard(?:\/|$)/i,
    /^\/settings(?:\/|$)/i,
    /^\/profile(?:\/|$)/i,
    /^\/manager(?:\/|$)/i,
    /^\/booking-details(?:\/|$)/i,
    /^\/spot-changes(?:\/|$)/i,
    /^\/link-expired(?:\/|$)/i,
    /^\/hiring(?:\/|$)/i,
    /^\/locations(?:\/|$)/i,
];
var buildNoIndexMeta = function (canonicalPath) { return ({
    routeKey: 'private',
    title: 'Truck Parking Hub',
    description: 'Secure truck parking operations and booking tools.',
    canonicalPath: canonicalPath,
    ogImage: DEFAULT_OG_IMAGE,
    robots: 'noindex,nofollow',
    schemaType: 'WebPage',
}); };
var isBlogPublic = function () { return process.env.REACT_APP_BLOG_ENABLED === 'true'; };
export var getRouteSeoMeta = function (pathname) {
    if (pathname === void 0) { pathname = '/'; }
    var normalizedPath = normalizePathname(pathname);
    if (PUBLIC_STATIC_ROUTE_META[normalizedPath]) {
        return PUBLIC_STATIC_ROUTE_META[normalizedPath];
    }
    var truckParkingRouteMeta = parseTruckParkingPath(normalizedPath);
    if (truckParkingRouteMeta.isTruckParkingRoute) {
        var state = truckParkingRouteMeta.stateSlug
            ? getLocationStateBySlug(truckParkingRouteMeta.stateSlug)
            : null;
        if (truckParkingRouteMeta.routeType === 'state') {
            if (!state) {
                return __assign(__assign({}, buildNoIndexMeta(normalizedPath)), { routeKey: 'truck_parking_state_invalid', sectionKey: 'locations' });
            }
            var stateMetaOverride = STATE_ROUTE_META_OVERRIDES[truckParkingRouteMeta.stateSlug] || null;
            return {
                routeKey: 'truck_parking_state',
                title: "Truck Parking in ".concat(state.name, " | Truck Parking Hub"),
                description: (stateMetaOverride === null || stateMetaOverride === void 0 ? void 0 : stateMetaOverride.description)
                    || "Browse truck parking hubs in ".concat(state.name, ", compare cities, and move into active lot pricing pages with secure parking options."),
                canonicalPath: buildTruckParkingStatePath(truckParkingRouteMeta.stateSlug),
                ogImage: DEFAULT_OG_IMAGE,
                robots: 'index,follow',
                schemaType: 'CollectionPage',
                sectionKey: 'locations',
            };
        }
        if (truckParkingRouteMeta.routeType === 'city') {
            if (!state) {
                return __assign(__assign({}, buildNoIndexMeta(normalizedPath)), { routeKey: 'truck_parking_city_invalid', sectionKey: 'locations' });
            }
            var cityLabel = decodeSlugToken(truckParkingRouteMeta.citySlug);
            var cityMetaOverride = CITY_ROUTE_META_OVERRIDES["".concat(truckParkingRouteMeta.stateSlug, "/").concat(truckParkingRouteMeta.citySlug)] || null;
            return {
                routeKey: 'truck_parking_city',
                title: "Truck Parking in ".concat(cityLabel, ", ").concat(state.name, " | Truck Parking Hub"),
                description: (cityMetaOverride === null || cityMetaOverride === void 0 ? void 0 : cityMetaOverride.description)
                    || "Explore truck parking options in ".concat(cityLabel, ", ").concat(state.name, ", compare active lots, and move directly into live pricing and booking routes."),
                canonicalPath: buildTruckParkingCityPath({
                    stateSlug: truckParkingRouteMeta.stateSlug,
                    citySlug: truckParkingRouteMeta.citySlug,
                }),
                ogImage: DEFAULT_OG_IMAGE,
                robots: 'index,follow',
                schemaType: 'CollectionPage',
                sectionKey: 'locations',
            };
        }
        if (truckParkingRouteMeta.routeType === 'intent') {
            if (!state) {
                return __assign(__assign({}, buildNoIndexMeta(normalizedPath)), { routeKey: 'truck_parking_intent_invalid', sectionKey: 'locations' });
            }
            var cityLabel = decodeSlugToken(truckParkingRouteMeta.citySlug);
            var intentSlug = String(truckParkingRouteMeta.intentSlug || '');
            var intentLabel = intentSlug.startsWith('near-')
                ? "Truck Parking Near ".concat(decodeSlugToken(intentSlug.replace(/^near-/, '')))
                : (intentSlug === 'overnight'
                    ? 'Overnight Truck Parking'
                    : intentSlug === 'secure'
                        ? 'Secure Truck Parking'
                        : intentSlug === 'monthly'
                            ? 'Monthly Truck Parking'
                            : 'Truck Parking');
            return {
                routeKey: 'truck_parking_intent',
                title: "".concat(intentLabel, " in ").concat(cityLabel, ", ").concat(state.name, " | Truck Parking Hub"),
                description: "Browse ".concat(intentLabel.toLowerCase(), " options in ").concat(cityLabel, ", ").concat(state.name, " and move from city intent hubs into active lot pricing pages."),
                canonicalPath: buildTruckParkingIntentPath({
                    stateSlug: truckParkingRouteMeta.stateSlug,
                    citySlug: truckParkingRouteMeta.citySlug,
                    intentSlug: intentSlug,
                }),
                ogImage: DEFAULT_OG_IMAGE,
                robots: 'index,follow',
                schemaType: 'CollectionPage',
                sectionKey: 'locations',
            };
        }
    }
    var legacyDiscoveryRoute = parseLegacyDiscoveryPath(normalizedPath);
    if (legacyDiscoveryRoute.isLegacyDiscoveryRoute) {
        var stateSlug = legacyDiscoveryRoute.stateSlug || '';
        var state = getLocationStateBySlug(stateSlug);
        var canonicalPath = state
            ? getLocationStatePathBySlug(state.slug)
            : TRUCK_PARKING_BASE_PATH;
        return __assign(__assign({}, buildNoIndexMeta(canonicalPath)), { routeKey: state ? 'legacy_locations_state' : 'legacy_locations', canonicalPath: canonicalPath, sectionKey: 'locations' });
    }
    var pricingPathMeta = parsePricingPath(normalizedPath);
    if (pricingPathMeta.isPricingRoute) {
        if (pricingPathMeta.routeType === 'legacy') {
            return __assign(__assign({}, buildNoIndexMeta(normalizedPath)), { routeKey: 'pricing_legacy' });
        }
        return {
            routeKey: 'pricing',
            title: 'Truck Parking Pricing | Truck Parking Hub',
            description: 'Review parking options and rates for this location, including daily, weekly, and monthly pricing.',
            canonicalPath: normalizedPath,
            ogImage: DEFAULT_OG_IMAGE,
            robots: 'index,follow',
            schemaType: 'Product',
        };
    }
    if (/^\/pm-partners\/[^/]+$/i.test(normalizedPath)) {
        return {
            routeKey: 'pm_partner_detail',
            title: 'Truck Repair Partner Profile | PM Services',
            description: 'Review this PM partner profile, service capabilities, and direct contact details for truck repair and preventive maintenance.',
            canonicalPath: normalizedPath.toLowerCase(),
            ogImage: DEFAULT_OG_IMAGE,
            robots: 'index,follow',
            schemaType: 'Service',
            sectionKey: 'pm_partners',
        };
    }
    if (/^\/blog(?:\/|$)/i.test(normalizedPath)) {
        if (!isBlogPublic())
            return buildNoIndexMeta(normalizedPath);
        return {
            routeKey: 'blog',
            title: 'Truck Parking Guides | Truck Parking Hub',
            description: 'Practical truck parking guides for drivers and lot owners.',
            canonicalPath: normalizedPath,
            ogImage: DEFAULT_OG_IMAGE,
            robots: 'index,follow',
            schemaType: 'BlogPosting',
        };
    }
    if (PRIVATE_ROUTE_PATTERNS.some(function (pattern) { return pattern.test(normalizedPath); })) {
        return buildNoIndexMeta(normalizedPath);
    }
    return __assign(__assign({}, buildNoIndexMeta(normalizedPath)), { routeKey: 'fallback' });
};
export var getCanonicalUrl = function (pathname, baseUrl) {
    if (pathname === void 0) { pathname = '/'; }
    if (baseUrl === void 0) { baseUrl = CANONICAL_BASE_URL; }
    var meta = getRouteSeoMeta(pathname);
    return "".concat(baseUrl).concat(meta.canonicalPath);
};
export var isNoIndexRoute = function (pathname) {
    if (pathname === void 0) { pathname = '/'; }
    return getRouteSeoMeta(pathname).robots === 'noindex,nofollow';
};
export default {
    CANONICAL_BASE_URL: CANONICAL_BASE_URL,
    getRouteSeoMeta: getRouteSeoMeta,
    getCanonicalUrl: getCanonicalUrl,
    isNoIndexRoute: isNoIndexRoute,
};
